import React, { useEffect, useState } from 'react';
import { Snackbar, Box,
    Card,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    IconButton,
    MenuItem as MuiMenuItem,
    Select,
    FormControl,
    InputLabel, } from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { MenuItem, menuItemsAPI, mealsAPI, Meal } from '../../services/api';

const ManagerMenu: React.FC = () => {
    const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
    const [meals, setMeals] = useState<Meal[]>([]);
    const [loading, setLoading] = useState(true);
    const [openModal, setOpenModal] = useState(false);
    const [formData, setFormData] = useState({
        mealId: '',
        date: new Date().toISOString().split('T')[0],
        mealType: 'lunch' as 'lunch' | 'dinner',
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [menuData, mealsData] = await Promise.all([
                menuItemsAPI.getMenuItems(),
                mealsAPI.getMeals(),
            ]);
            setMenuItems(menuData);
            setMeals(mealsData);
        } catch (err: any) {
            setError('خطا در بارگذاری منو');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            await menuItemsAPI.createMenuItem(formData);
            setSuccess('آیتم منو با موفقیت اضافه شد');
            setOpenModal(false);
            setFormData({
                mealId: '',
                date: new Date().toISOString().split('T')[0],
                mealType: 'lunch',
            });
            loadData();
        } catch (err: any) {
            setError('خطا در اضافه کردن آیتم منو');
        }
    };

    const handleDelete = async (menuItemId: string) => {
        if (!window.confirm('آیا از حذف این آیتم منو مطمئن هستید؟')) return;

        try {
            await menuItemsAPI.deleteMenuItem(menuItemId);
            setSuccess('آیتم منو با موفقیت حذف شد');
            loadData();
        } catch (err: any) {
            setError('خطا در حذف آیتم منو');
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 4,
                }}
            >
                <Typography
                    variant="h5"
                    sx={{
                        fontFamily: 'Vazir, sans-serif',
                        fontWeight: 600,
                    }}
                >
                    مدیریت منو
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenModal(true)}
                    sx={{ fontFamily: 'Vazir, sans-serif' }}
                >
                    افزودن به منو
                </Button>
            </Box>

            <Snackbar open={!!error} autoHideDuration={2000} onClose={() => { if(typeof setError === 'function') setError(''); }} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
  <Alert severity="error" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => { if(typeof setError === 'function') setError(''); }}>
    {error}
  </Alert>
</Snackbar>
            <Snackbar open={!!success} autoHideDuration={2000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
  <Alert severity="success" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => setSuccess('')}>
    {success}
  </Alert>
</Snackbar>

            <Card>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                                    تاریخ
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                                    نوع وعده
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                                    غذا
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                                    عملیات
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {menuItems.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontFamily: 'Vazir, sans-serif' }}
                                        >
                                            هیچ آیتم منویی یافت نشد
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                menuItems.map((item) => {
                                    const meal = meals.find((m) => m.id === item.mealId);
                                    // Parse date as local date (YYYY-MM-DD) to avoid timezone shift
                                    const dateStr = item.date.split('T')[0];
                                    const dateParts = dateStr.split('-');
                                    const localDate = dateParts.length === 3
                                        ? new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]))
                                        : new Date(item.date);
                                    return (
                                        <TableRow key={item.id} hover>
                                            <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>
                                                {localDate.toLocaleDateString('fa-IR')}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>
                                                {item.mealType === 'lunch' ? 'ناهار' : 'شام'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>
                                                {meal?.title || '-'}
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    color="error"
                                                    size="small"
                                                    onClick={() => handleDelete(item.id)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            <Dialog
                open={openModal}
                onClose={() => setOpenModal(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                    },
                }}
            >
                <DialogTitle sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                    افزودن به منو
                </DialogTitle>
                <Box component="form" onSubmit={handleSubmit}>
                    <DialogContent>
                        <TextField
                            fullWidth
                            label="تاریخ"
                            type="date"
                            value={formData.date}
                            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                            required
                            sx={{ mb: 2 }}
                            InputLabelProps={{
                                shrink: true,
                                sx: { fontFamily: 'Vazir, sans-serif' },
                            }}
                            inputProps={{
                                sx: { fontFamily: 'Vazir, sans-serif' },
                            }}
                        />
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel sx={{ fontFamily: 'Vazir, sans-serif' }}>نوع وعده</InputLabel>
                            <Select
                                value={formData.mealType}
                                onChange={(e) => setFormData({ ...formData, mealType: e.target.value as 'lunch' | 'dinner' })}
                                required
                                label="نوع وعده"
                                sx={{ fontFamily: 'Vazir, sans-serif' }}
                            >
                                <MuiMenuItem value="lunch" sx={{ fontFamily: 'Vazir, sans-serif' }}>
                                    ناهار
                                </MuiMenuItem>
                                <MuiMenuItem value="dinner" sx={{ fontFamily: 'Vazir, sans-serif' }}>
                                    شام
                                </MuiMenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel sx={{ fontFamily: 'Vazir, sans-serif' }}>غذا</InputLabel>
                            <Select
                                value={formData.mealId}
                                onChange={(e) => setFormData({ ...formData, mealId: e.target.value })}
                                required
                                label="غذا"
                                sx={{ fontFamily: 'Vazir, sans-serif' }}
                            >
                                {meals.map((meal) => (
                                    <MuiMenuItem key={meal.id} value={meal.id} sx={{ fontFamily: 'Vazir, sans-serif' }}>
                                        {meal.title}
                                    </MuiMenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button
                            onClick={() => setOpenModal(false)}
                            sx={{ fontFamily: 'Vazir, sans-serif' }}
                        >
                            انصراف
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            sx={{ fontFamily: 'Vazir, sans-serif' }}
                        >
                            ذخیره
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </Box>
    );
};

export default ManagerMenu;
